import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { logoutApi } from 'Redux/Login/login.actions'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Popover from '@material-ui/core/Popover'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import toast from 'react-hot-toast'
import './style.css'

import environment from 'environments/environment'
import { socket } from 'Utils/web-socket'
import eventBus from 'NewChat/helpers/eventEmitter'
import { getClinicList } from 'Redux/Clinic/clinic.actions'
import SwitchClinic from './ClinicList'

const systemName = environment.systemName

/**
 * @fileOverview Manages Routing.
 * @component
 * @author Akhil Francis <akhil@trillium-technologies.com>
 * @author Ridhik Govind <ridhik@tensaw.email>
 * @return {JSXElement}
 * @example
 * return(
 * <DefaultLayout/>
 * )
 */

const useStyles = makeStyles((theme) => ({
  icon: { fontSize: '2rem !important' },
  popover: { pointerEvents: 'none' },
  popoverContent: { pointerEvents: 'auto' },
}))

function Header(props) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  let administratorAccess =
    JSON.parse(localStorage.getItem('isAdministratorAccess')) || false

  const [chatCount, setChatCount] = useState(0)
  const [messagesId, setMessagesId] = useState([])
  const [userData] = useState(JSON.parse(localStorage.getItem('user_data')))
  const [newMenuName, setNewMenuName] = useState('')
  const [openedPopover, setOpenedPopover] = useState(false)
  const popoverAnchor = useRef(null)
  const [conversation, setConversation] = useState({})
  const [selectedClinic, setSelectedClinic] = useState(userData?.clinicId)
  const [clinics, setClinics] = useState([])

  const popoverEnter = () => setOpenedPopover(true)
  const popoverLeave = () => setOpenedPopover(false)

  // fetch clinic list
  useEffect(() => {
    props.getClinicList()
  }, [])

  // Set clinic list
  useEffect(() => {
    if (props.clinicData && props.clinicData.clinicList) {
      const { clinicList } = props.clinicData
      localStorage.setItem('clinicList', JSON.stringify(clinicList))
      setClinics(clinicList)

      const currentClinic = clinicList?.find(
        (clinic) => clinic.clinicId === userData.clinicId,
      )
      if (currentClinic) setSelectedClinic(currentClinic.clinicId)
    }
  }, [props.clinicData.clinicList])

  // ✅ MAIN ACTIVE MENU HANDLER
  useEffect(() => {
    const path = location.pathname

    if (path.includes('/authorizations')) setNewMenuName('authorizations')
    else if (path.includes('/accounts')) setNewMenuName('accounts')
    else if (path.includes('/appointments')) setNewMenuName('appointments')
    else if (path.includes('/reviews')) setNewMenuName('reviews')
    else if (path.includes('/claims')) setNewMenuName('claims')
    else if (path.includes('/chat')) setNewMenuName('chat')
    else if (path.includes('/reports')) setNewMenuName('reports')
    else if (path.includes('/admin')) setNewMenuName('admin')
    else setNewMenuName('dashboard')
  }, [location.pathname])

  useEffect(() => {
    document.addEventListener('DOMContentLoaded', () => {
      if (!Notification) {
        alert('Desktop notifications not available.')
        return
      }
      if (Notification.permission !== 'granted') {
        Notification.requestPermission()
      }
    })
  }, [])

  
  useEffect(() => {
    if (
      props.LoginData &&
      props.LoginData.logout &&
      props.LoginData.logout.response &&
      props.LoginData.logout.response.data
    ) {
      const { data } = props.LoginData.logout.response
      if (data) window.location.href = '/login'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.LoginData, history])

  eventBus.on('select_convo', (conversation) => {
    setConversation({
      conversationId: conversation._id,
      userId: props.newChatData._id,
    })
  })

  const messageHandler = (data) => {
    if (data.fromUserId !== userData.userId) {
      new Notification(`${data.fromUserName}`, { body: `${data.message}` })

      if (!window.location.href.includes('/chat')) {
        toast.success('New Message Received', { duration: 6000 })

        if (!messagesId.includes(data.conversationSessionId)) {
          setMessagesId((prev) => [...prev, data.conversationSessionId])
          setChatCount((prev) => prev + 1)
        }
      }
    }
  }

  const redirectToMenuItems = (menuName) => {
    const routes = {
      dashboard: '/dashboard',
      settings: '/settings',
      chat: '/chat',
      authorizations: '/authorizations/list',
      claims: '/claims',
      appointments: '/appointments',
      reviews: '/reviews',
      accounts: '/accounts/patient-list',
      reports: '/reports/authorization',
      admin: '/admin',
    }
    history.push(routes[menuName])
  }

  // Logout handler
  const logout = () => {
    socket.emit('leave conversation', {
      conversationId: conversation.conversationId,
      userId: conversation.userId,
    })

    socket.emit('clinic_logout', {
      clinicId: userData.clinicId,
      userId: userData.userId,
    })

    props.logoutApi(localStorage.getItem('refreshToken'))
  }

  // Unread chat count handler
  useEffect(() => {
    if (props.chatData?.inboxHistory) {
      let count = 0
      props.chatData.inboxHistory.filteredChatMessages.forEach((x) => {
        if (x.lastMessageStatus !== 2 && x.fromUserId !== userData.userId) {
          count++
        }
      })
      setChatCount(count)
    }
  }, [props.chatData?.inboxHistory])

  const nameLetters = (x) => {
    if (!x) return ''
    const parts = x.split(' ')
    return parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0][0]
  }

  return (
    <div className="clinic__header">
      <div className="clinic__header--left">
        <ul>
          <li
            className={newMenuName === 'dashboard' ? 'selected' : null}
            onClick={() => redirectToMenuItems('dashboard')}
          >
            Scheduler
          </li>

          <li
            className={newMenuName === 'chat' ? 'selected' : null}
            onClick={() => redirectToMenuItems('chat')}
          >
            Chats
          </li>

          <li
            className={newMenuName === 'authorizations' ? 'selected' : null}
            onClick={() => redirectToMenuItems('authorizations')}
          >
            Authorizations
          </li>

          {systemName === 'System dev' && (
            <li
              className={newMenuName === 'claims' ? 'selected' : null}
              onClick={() => redirectToMenuItems('claims')}
            >
              Claims
            </li>
          )}

          <li
            className={
              newMenuName === 'appointments' ||
              location.pathname.includes('/appointments')
                ? 'selected'
                : null
            }
            onClick={() => redirectToMenuItems('appointments')}
          >
            Appointments
          </li>

          <li
            className={newMenuName === 'reviews' ? 'selected' : null}
            onClick={() => redirectToMenuItems('reviews')}
          >
            Reviews
          </li>

          <li
            className={
              newMenuName === 'accounts' ||
              location.pathname.includes('/accounts/patient-details')
                ? 'selected'
                : null
            }
            onClick={() => redirectToMenuItems('accounts')}
          >
            Accounts
          </li>

          {administratorAccess && (
            <li
              className={newMenuName === 'reports' ? 'selected' : null}
              onClick={() => redirectToMenuItems('reports')}
            >
              Reports
            </li>
          )}

          {administratorAccess && (
            <li
              className={newMenuName === 'admin' ? 'selected' : null}
              onClick={() => redirectToMenuItems('admin')}
            >
              Admin
            </li>
          )}
        </ul>
      </div>

      <div
        className="clinic__header--left"
        onMouseEnter={popoverEnter}
        onMouseLeave={popoverLeave}
      >
        <div
          className="clinic__profileCircle"
          ref={popoverAnchor}
        >
          <span>{nameLetters(userData?.clinicName)}</span>
        </div>
        <KeyboardArrowDownIcon color="primary" />
      </div>

      <Popover
        className={classes.popover}
        classes={{ paper: classes.popoverContent }}
        open={openedPopover}
        anchorEl={popoverAnchor.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ onMouseEnter: popoverEnter, onMouseLeave: popoverLeave }}
      >
        <div className="clinicHeader__popover">
          <SwitchClinic
            clinics={clinics}
            setOpenedPopover={setOpenedPopover}
            setSelectedClinic={setSelectedClinic}
            selectedClinic={selectedClinic}
          />

          <div
            className={
              localStorage.getItem('isInternalUser') == 1
                ? 'clinicHeader__popover--listInternal'
                : 'clinicHeader__popover--list'
            }
          >
            <div onClick={() => redirectToMenuItems('settings')}>Settings</div>
            <div className="logout" onClick={logout}>
              Logout
            </div>
          </div>
        </div>
      </Popover>
    </div>
  )
}

const mapStateToProps = (state) => ({
  LoginData: state.login,
  chatData: state.chat,
  newChatData: state.newChat,
  clinicData: state.clinic,
})

const mapDispatchToProps = (dispatch) => ({
  logoutApi: (values) => dispatch(logoutApi(values)),
  getClinicList: (clinicID) => dispatch(getClinicList(clinicID)),
})

Header.propTypes = {
  logoutApi: PropTypes.func,
  getClinicList: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(Header)
